import classNames from "classnames"
import { Link } from "gatsby"
import React from "react"

import { Color } from "../../../../constants/V2/color"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"

import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import PriceDisplay from "@components/elements/V2/PriceDisplay"
import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import {
  getAccentColor,
  getBackgroundColorClass,
  getTextColorClass,
} from "@utils/V2/color"

type Props = {
  imageUrl: string
  imageAlt: string
  productName: string
  productDescription: string
  priceValue?: string | number
  detailLink?: string
  backgroundColor?: Color
}

const ProductPreview = ({
  imageUrl,
  imageAlt,
  productName,
  productDescription,
  priceValue,
  detailLink,
  backgroundColor = Color.Charcoal,
  ...props
}: Props) => {
  const { animationRefCallback } = useScrollAnimateSelf(true)
  return (
    <div
      ref={animationRefCallback}
      className={classNames(
        getBackgroundColorClass(backgroundColor),
        "col-span-4 flex flex-col rounded-20 sm:col-span-2 md:col-span-3 md:rounded-24 lg:col-span-4 lg:rounded-32"
      )}
      {...props}
    >
      <div className="relative flex flex-none items-center justify-center">
        <GatsbyStoryblokImage
          aspectRatio="1:1"
          loadType="eager"
          image={imageUrl}
          alt={imageAlt || "Product image"}
          width={245}
          testId="product-preview-card-image"
        />
      </div>

      <div className="flex flex-grow flex-col justify-between px-20 pb-24 pt-8 md:px-24 md:pb-32 md:pt-16 lg:px-32 lg:pb-40">
        <div className="mb-8 flex-grow">
          <Typography
            font="grotesk"
            weight="medium"
            size="body-lg-fixed"
            color={getAccentColor(backgroundColor)}
            text={productName}
            className="mb-8"
          />
          <Typography
            font="grotesk"
            weight="book"
            size="body-md"
            color={getAccentColor(backgroundColor)}
            text={productDescription}
          />
        </div>
        <div className="flex flex-row justify-between">
          <PriceDisplay
            priceFontSize="body-lg"
            productPrices={{ price: priceValue as string }}
            priceColor={getAccentColor(backgroundColor)}
            showDecimals={false}
            className="opacity-80"
          />
          {detailLink ? (
            <Link to={detailLink}>
              <ArrowIcon
                className={classNames(
                  "h-full opacity-15 hover:cursor-pointer hover:opacity-100",
                  getTextColorClass(getAccentColor(backgroundColor))
                )}
              />
            </Link>
          ) : (
            <Typography
              text="Coming Soon"
              size="body-lg"
              font="grotesk"
              weight="book"
              color={getAccentColor(backgroundColor)}
              className="opacity-15"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductPreview
